import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Utensils, Droplets, Star, Check, ChevronDown, ChevronUp, Zap, Trophy, Footprints, Coffee, Activity, ChefHat, Sunrise, Timer, Salad } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth, api } from '../context/AppContext';
import { toast } from 'sonner';

const ICON_MAP = {
  shield: Shield, utensils: Utensils, droplets: Droplets, star: Star,
  footprints: Footprints, coffee: Coffee, activity: Activity,
  'chef-hat': ChefHat, sunrise: Sunrise, timer: Timer, salad: Salad,
};

const PILLAR_STYLES = {
  'green-500': {
    bg: 'bg-green-500/10', border: 'border-green-500/30',
    iconBg: 'bg-green-500/20', iconText: 'text-green-500',
    text: 'text-green-500', bar: 'bg-green-500', barDim: 'bg-green-500/60',
  },
  'blue-500': {
    bg: 'bg-blue-500/10', border: 'border-blue-500/30',
    iconBg: 'bg-blue-500/20', iconText: 'text-blue-500',
    text: 'text-blue-500', bar: 'bg-blue-500', barDim: 'bg-blue-500/60',
  },
  'cyan-500': {
    bg: 'bg-cyan-500/10', border: 'border-cyan-500/30',
    iconBg: 'bg-cyan-500/20', iconText: 'text-cyan-500',
    text: 'text-cyan-500', bar: 'bg-cyan-500', barDim: 'bg-cyan-500/60',
  },
};

function PillarBar({ pillar, icon: IconComp, color, index }) {
  const completed = pillar.completed;
  const s = PILLAR_STYLES[color] || PILLAR_STYLES['green-500'];
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08 }}
      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${completed ? `${s.bg} ${s.border}` : 'bg-secondary/30 border-border/50'}`}
      data-testid={`retention-pillar-${pillar.icon}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${completed ? `${s.iconBg} ${s.iconText}` : 'bg-secondary text-muted-foreground'}`}>
        {completed ? <Check className="w-5 h-5" /> : <IconComp className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold text-foreground truncate pr-2">{pillar.name_fr}</p>
          <span className={`text-[10px] font-bold shrink-0 ${completed ? s.text : 'text-muted-foreground'}`}>
            {completed ? `+${pillar.points}` : `${pillar.progress}%`}
          </span>
        </div>
        <div className="w-full h-1.5 rounded-full bg-secondary overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${completed ? s.bar : s.barDim}`}
            initial={{ width: 0 }}
            animate={{ width: `${pillar.progress}%` }}
            transition={{ duration: 0.8, delay: index * 0.1 }}
          />
        </div>
        <p className="text-[9px] text-muted-foreground mt-0.5">{pillar.desc_fr}</p>
      </div>
    </motion.div>
  );
}

function SpecialMissionCard({ mission, onValidate, validating }) {
  const IconComp = ICON_MAP[mission.icon] || Star;
  const completed = mission.completed;
  const isHonor = mission.type === 'honor';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className={`p-3 rounded-xl border-2 border-dashed transition-all ${completed ? 'bg-amber-500/10 border-amber-500/30' : 'bg-gradient-to-r from-amber-500/5 to-orange-500/5 border-amber-500/20'}`}
      data-testid="retention-special-mission"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${completed ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-500/10 text-amber-500/70'}`}>
          {completed ? <Check className="w-5 h-5" /> : <IconComp className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[8px] uppercase tracking-widest text-amber-500 font-bold">Mission Speciale</span>
            <Zap className="w-3 h-3 text-amber-400" />
          </div>
          <p className="text-xs font-semibold text-foreground mt-0.5">{mission.name_fr}</p>
        </div>
        <span className={`text-xs font-bold shrink-0 ${completed ? 'text-amber-400' : 'text-muted-foreground'}`}>
          {completed ? `+${mission.points}` : `${mission.points}pts`}
        </span>
      </div>
      {isHonor && !completed && (
        <Button
          onClick={onValidate}
          disabled={validating}
          size="sm"
          className="w-full mt-2.5 h-8 text-xs bg-amber-500/90 hover:bg-amber-500 text-black font-bold"
          data-testid="validate-special-btn"
        >
          {validating ? 'Validation...' : "J'ai complete ce defi!"}
        </Button>
      )}
      {!isHonor && !completed && (
        <Button
          onClick={onValidate}
          disabled={validating}
          size="sm"
          variant="outline"
          className="w-full mt-2.5 h-8 text-xs border-amber-500/40 text-amber-500 hover:bg-amber-500/10 font-bold"
          data-testid="validate-special-manual-btn"
        >
          {validating ? 'Validation...' : "J'ai complete ce defi!"}
        </Button>
      )}
    </motion.div>
  );
}

export function RetentionChallenges() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [validating, setValidating] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/retention/weekly-status');
      if (res.data.active) {
        setData(res.data);
      } else {
        setData(null);
      }
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) fetchStatus(); }, [user, fetchStatus]);

  const handleValidateSpecial = async () => {
    setValidating(true);
    try {
      const res = await api.post('/retention/validate-special');
      if (res.data.success) {
        toast.success(`Mission speciale validee! +${res.data.points_earned} pts`);
        fetchStatus();
      }
    } catch {
      toast.error('Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  if (loading || !data) return null;

  const pillars = data.pillars;
  const pillarColors = {
    steel_wall: 'green-500',
    metronome: 'blue-500',
    reservoir: 'cyan-500',
  };
  const pillarOrder = ['steel_wall', 'metronome', 'reservoir'];
  const completedCount = pillarOrder.filter(k => pillars[k]?.completed).length + (data.special_mission?.completed ? 1 : 0);

  return (
    <Card className="border-primary/20 bg-gradient-to-b from-primary/5 to-transparent overflow-hidden" data-testid="retention-challenges-card">
      <CardContent className="p-0">
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-4 pb-3 text-left hover:bg-primary/5 transition-colors"
          data-testid="retention-header"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground tracking-tight">Defis de la Semaine</h3>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Sem. du {data.week_start} — Jour {data.days_elapsed}/7
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="text-sm font-bold text-primary">{data.total_earned}<span className="text-[10px] text-muted-foreground">/{data.total_possible}</span></p>
              <p className="text-[9px] text-muted-foreground">{completedCount}/4 completes</p>
            </div>
            {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </div>
        </button>

        {/* Content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-2">
                {/* 3 Fixed Pillars */}
                {pillarOrder.map((key, i) => {
                  const p = pillars[key];
                  const IconComp = ICON_MAP[p.icon] || Shield;
                  return (
                    <PillarBar key={key} pillar={p} icon={IconComp} color={pillarColors[key]} index={i} />
                  );
                })}

                {/* Special Mission */}
                <SpecialMissionCard
                  mission={data.special_mission}
                  onValidate={handleValidateSpecial}
                  validating={validating}
                />

                {/* All complete celebration */}
                {data.all_complete && (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="p-3 rounded-xl bg-gradient-to-r from-primary/20 to-amber-500/20 border border-primary/30 text-center"
                  >
                    <p className="text-xs font-bold text-primary">SEMAINE PARFAITE — {data.total_possible} PTS</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Tous les piliers sont solides. Discipline d'acier.</p>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
